import { Layout } from "providers/Layout";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  Input
} from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Card } from "shared/components/Card";
import { useSettingsController } from "views/settings-integration/controller";
import { Delete01Icon, PlusSignIcon } from "@hugeicons-pro/core-stroke-rounded";
import {
  arraysEqual,
  normalizeToArray
} from "views/settings-integration/integrations/google-analytics/helpers";

export const GoogleAnalyticsView = () => {
  const navigate = useNavigate();
  const { updateSettings, currentSettings, isUpdateSettingsPending } =
    useSettingsController();

  const [analyticsIds, setAnalyticsIds] = useState<string[]>([]);

  const currentIds = useMemo(
    () =>
      normalizeToArray(currentSettings.google.googleAnalyticsId).filter((id) =>
        id.trim()
      ),
    [currentSettings.google.googleAnalyticsId]
  );

  const hasChanges = useMemo(() => {
    const newIds = analyticsIds.filter((id) => id.trim());
    return !arraysEqual(currentIds, newIds);
  }, [analyticsIds, currentIds]);

  const handleSave = () => {
    const validIds = analyticsIds.map((id) => id.trim());

    updateSettings({
      ...currentSettings.google,
      googleAnalyticsId: validIds.length > 0 ? validIds : null
    }).then(() => {
      if (validIds.length === 0) navigate("/settings/integration");
    });
  };

  const handleCancel = () => {
    setAnalyticsIds(normalizeToArray(currentSettings.google.googleAnalyticsId));
  };

  const handleIdChange = (index: number, value: string) => {
    const newIds = [...analyticsIds];
    newIds[index] = value;
    setAnalyticsIds(newIds);
  };

  const handleRemoveId = (index: number) => {
    setAnalyticsIds(analyticsIds.filter((_, i) => i !== index));
  };

  const handleAddId = () => {
    setAnalyticsIds([...analyticsIds, ""]);
  };

  useEffect(() => {
    setAnalyticsIds(normalizeToArray(currentSettings.google.googleAnalyticsId));
  }, [currentSettings.google.googleAnalyticsId]);

  return (
    <Layout
      header={
        <>
          <div className="flex items-center justify-between border-b border-alpha-40 px-3 md:px-6 pb-3">
            <BreadCrumbs
              crumbs={[
                { title: "Settings", href: "/settings" },
                { title: "Integration", href: "/settings/integration" },
                {
                  title: "Google Analytics",
                  href: "/settings/integration/google-analytics"
                }
              ]}
            />
          </div>
          <h1 className="text-xl font-bold text-content-1000 p-6 pb-3">
            Google Analytics
          </h1>
        </>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={handleCancel}
            className="w-[125px]"
            disabled={!hasChanges || isUpdateSettingsPending}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            onClick={handleSave}
            className="w-[125px]"
            disabled={!hasChanges || isUpdateSettingsPending}
            loading={isUpdateSettingsPending}
          >
            Save
          </Button>
        </div>
      }
    >
      <Card className="w-full p-6 max-w-[800px]">
        <p className="font-bold">Credentials</p>

        <div className="mt-6 border-t border-alpha-10 pt-6 flex flex-col">
          {analyticsIds.map((id, idx) => (
            <div key={idx} className="flex gap-2 w-full mb-4">
              <div className="flex items-center justify-center shrink-0 border border-alpha-40 rounded-lg w-11 h-12 bg-alpha-40">
                <p className="font-medium text-base text-content-1000">
                  {idx + 1}
                </p>
              </div>
              <Input
                value={id}
                onChange={(e) => handleIdChange(idx, e.target.value)}
                id={`gId-${idx}`}
                placeholder="G-XYZ"
                wrapperClassName="w-full"
                required
              />
              <IconButton
                className="shrink-0"
                HugeIcon={Delete01Icon}
                onClick={() => handleRemoveId(idx)}
              />
            </div>
          ))}
        </div>

        <Button
          size={ButtonSize.SM}
          HugeIcon={PlusSignIcon}
          className="mt-6"
          onClick={handleAddId}
        >
          Add more
        </Button>
      </Card>
    </Layout>
  );
};
